// Create.page.jsx - Token Creation Page with AI Generation

import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Container,
    Paper,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListSubheader,
    useTheme,
    Divider,
    Card,
    CardContent,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    AutoAwesome as AutoAwesomeIcon,
    RocketLaunch as RocketLaunchIcon,
    ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

import { API_BASE_URL } from '../utils/constants';
import { WalletsContext } from '../state/context/wallets.context';
import { toast } from 'react-toastify';
import apiCall from '../utils/apiCall';


const TOKEN_API_ENDPOINT = `${ API_BASE_URL }/tokens/create`;

function CreatePage() {
    const theme = useTheme();

    // --- Context ---
    const { state: walletsState, getWallets } = useContext(WalletsContext);
    const walletList = walletsState?.wallets || [];
    const { mainWallet } = walletsState;



    // Filter favorite wallets using useMemo for optimization
    const favoriteWallets = useMemo(() => {
        let walletsList = walletList.filter((wallet) => wallet.isFavorite);
        if (mainWallet) {
            walletsList = [...walletsList, mainWallet];
        }
        return walletsList;
    }, [walletList, mainWallet]);


    // --- State for User Inputs ---
    const [nameInput, setNameInput] = useState('');
    const [tickerInput, setTickerInput] = useState('');
    const [descriptionInput, setDescriptionInput] = useState('');
    const [logoConceptInput, setLogoConceptInput] = useState('');
    const [amountInput, setAmountInput] = useState('');

    // --- State for Generated/Final Values ---
    const [finalName, setFinalName] = useState('');
    const [finalTicker, setFinalTicker] = useState('');
    const [finalDescription, setFinalDescription] = useState('');
    const [generatedLogoConcept, setGeneratedLogoConcept] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [selectedWalletOrGroupId, setSelectedWalletOrGroupId] = useState('');

    // --- State for Loading Indicators ---
    const [isGeneratingText, setIsGeneratingText] = useState(false);
    const [isGeneratingLogo, setIsGeneratingLogo] = useState(false);
    const [isCreatingToken, setIsCreatingToken] = useState(false);

    // --- State for Error Messages ---
    const [error, setError] = useState(null);

    // --- Effects ---
    useEffect(() => {
        if (!walletList.length) {
            getWallets();
        }
    }, []);


    // --- Handlers ---

    // Handle the selection change for the wallet/group dropdown
    const handleWalletChange = (e) => {
        setSelectedWalletOrGroupId(e.target.value);
    };

    // Handle the main "Generate Token" button click
    const handleGenerate = async () => {
        // Reset states before starting generation
        setError(null);
        setFinalName('');
        setFinalTicker('');
        setFinalDescription('');
        setGeneratedLogoConcept('');
        setLogoUrl('');
        setIsGeneratingText(true); // Start text generation loading
        setIsGeneratingLogo(false); // Ensure logo loading is off initially

        try {
            // --- Step 1: Generate Text Details ---
            // const textResponse = await fetch(`${ TOKEN_API_ENDPOINT }/generate_text_details`, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //     },
            //     // Send undefined if the input fields are empty string, so the backend knows to generate them.
            //     body: JSON.stringify({
            //         name: nameInput || undefined,
            //         ticker: tickerInput || undefined,
            //         description: descriptionInput || undefined,
            //         logo_concept: logoConceptInput || undefined,
            //     }),
            // });

            const textData = await apiCall('/tokens/create/generate_text_details', 'POST', {
                name: nameInput || undefined,
                ticker: tickerInput || undefined,
                description: descriptionInput || undefined,
                logo_concept: logoConceptInput || undefined,
            });


            setFinalName(textData.name);
            setFinalTicker(textData.ticker);
            setFinalDescription(textData.description);
            // Store the generated concept to allow logo regeneration based on it later
            setGeneratedLogoConcept(textData.logo_concept);

            setIsGeneratingText(false); // Text generation finished

            // --- Step 2: Automatically Trigger Logo Image Generation (if concept is available) ---
            if (textData.logo_concept) {
                // Use the generated concept to generate the logo
                handleGenerateLogo(textData.logo_concept);
            } else {
                // This case should ideally not happen if backend always returns a concept
                setError("Text generation did not provide a logo concept.");
            }

        } catch (err) {
            console.error("Generation Error:", err);
            setError(err.message); // Set the error message in state
            setIsGeneratingText(false); // Stop text loading
            setIsGeneratingLogo(false); // Ensure logo loading is also stopped in case it was triggered before error
        }
    };

    // Handle logo generation (used for initial generation and regeneration)
    const handleGenerateLogo = async (conceptToUse) => {
        // Prevent logo generation if no concept is available
        if (!conceptToUse) {
            setError("Logo concept is missing.");
            return;
        }

        setIsGeneratingLogo(true); // Start logo loading
        setLogoUrl(''); // Clear any previous logo URL while generating

        try {
            const logoResponse = await fetch(`${ TOKEN_API_ENDPOINT }/generate_logo`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Use the provided concept (either user input's concept or the AI generated one)
                body: JSON.stringify({
                    logo_concept: conceptToUse,
                }),
            });

            // Check if the logo generation API call was successful
            if (!logoResponse.ok) {
                const errorData = await logoResponse.json();
                // Throw an error with a more descriptive message
                throw new Error(`Logo generation failed: ${ errorData.error || `Status: ${ logoResponse.status } ${ logoResponse.statusText }` }`);
            }

            const logoData = await logoResponse.json();
            console.log("Logo Data:", logoData); // Log the logo data for debugging
            setLogoUrl(logoData.logo_url); // Store the generated image URL

        } catch (err) {
            console.error("Logo Generation Error:", err);
            setError(`Logo regeneration failed: ${ err.message }`); // Set logo specific error
            setLogoUrl(''); // Clear logo URL on error
        } finally {
            setIsGeneratingLogo(false); // Logo generation finished
        }
    };


    async function fetchAndUpload(data) {
        // // Step 1: Fetch data from the source URL
        // const response = await fetch(`${ TOKEN_API_ENDPOINT }/upload`, {
        //     method: 'POST',
        //     headers: {
        //         'Content-Type': 'application/json',
        //     },
        //     body: JSON.stringify(data),
        // });

        // if (!response.ok) {
        //     throw new Error(`HTTP error! status: ${ response.status }`);
        // }
        // const result = await response.json();
        // console.log("Fetched and uploaded data:", result);
        // return result.image

        return (await apiCall('/tokens/create/upload', 'POST', data)).image;

    }

    // Handler for the "Create Token" button (Placeholder logic)
    const handleCreateToken = async () => {
        // alert("Token creation is not implemented yet!"); // Placeholder alert
        setError(null); // Clear previous errors
        setIsCreatingToken(true); // Start token creation loading

        // Basic validation
        if (!finalName || !finalTicker || !finalDescription || !logoUrl || !selectedWalletOrGroupId) {
            setError("Please ensure all generated details, logo, amount, and wallet are selected.");
            setIsCreatingToken(false);
            return;
        }


        let targetWallets = [];

        // Check if it's an individual wallet
        const selectedWalletObj = walletList.find((wallet) => wallet._id === selectedWalletOrGroupId);
        if (selectedWalletObj) {
            targetWallets.push(selectedWalletObj);
        } else {
            // Otherwise, assume it's a group and get all wallets in that group
            targetWallets = walletList.filter((wallet) => wallet.group === selectedWalletOrGroupId);
            if (mainWallet && selectedWalletOrGroupId === "ODIN_MAIN_WALLET") {
                targetWallets.push(mainWallet);
            }
            if (targetWallets.length === 0) {
                // This case shouldn't happen if the select list is correct, but good to check
                toast.error("Selected group contains no wallets.");
                setIsCreatingToken(false);
                setError("Selected group contains no wallets.");
                return;
            }
        }
        const wallet = targetWallets[0]; // Assuming we only need the first wallet for the example



        let walletData = wallet.serializeIdentity; // Assuming this exists and works
        let chossenWallet = {};
        if (walletData) {
            chossenWallet = {
                walletData: walletData,
                type: "BTC"
            };
        } else {
            chossenWallet = {
                walletData: wallet.privateKey,
                type: "ICP"
            };
        }

        // --- Step 3: Call the backend API to create the token ---
        try {
            const image = await fetchAndUpload({
                url: logoUrl,
                ...chossenWallet
            });

            const tokenData = {
                name: finalName,
                ticker: finalTicker,
                description: finalDescription,
                twitter: "",
                telegram: "",
                website: "",
                buy: amountInput,
                image: image,
                discount: "",
                ...chossenWallet
            };

            console.log("Token Data:", tokenData);
            await apiCall(`/tokens/create/new`, 'POST', tokenData);

            // Assuming the API returns a success message or token details
            toast.success("Token created successfully!");


        } catch (err) {
            console.error("Token Creation Error:", err);
            setError(`Token creation failed: ${ err.message }`);
        } finally {
            setIsCreatingToken(false); // Stop token creation loading
        }
    };


    // --- Render ---
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Header Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        background: `linear-gradient(135deg, ${ theme.palette.primary.main }15, ${ theme.palette.secondary.main }15)`,
                        border: `1px solid ${ theme.palette.divider }`,
                        borderRadius: 2
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        <AutoAwesomeIcon sx={{ mr: 2, fontSize: 'inherit', color: theme.palette.primary.main }} />
                        AI Token Creator
                        <Box
                            component="span"
                            sx={{
                                fontSize: '0.6em',
                                color: theme.palette.warning.main,
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                ml: 2,
                                px: 1,
                                py: 0.5,
                                bgcolor: theme.palette.warning.light + '20',
                                borderRadius: 1,
                                border: `1px solid ${ theme.palette.warning.main }40`
                            }}
                        >
                            BETA
                        </Box>
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            mx: 'auto'
                        }}
                    >
                        Create your meme coin on Odin.fun with AI assistance! Provide optional inputs or let AI generate everything for you.
                    </Typography>
                </Paper>

                {/* Input Form Section */}
                <Paper elevation={1} sx={{ p: 4, border: `1px solid ${ theme.palette.divider }` }}>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, mb: 3 }}>
                        Token Details
                    </Typography>
                    <Stack spacing={3}>
                        <TextField
                            label="Token Name (Optional)"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputProps={{ maxLength: 30 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="Ticker (Optional)"
                            value={tickerInput}
                            onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                            fullWidth
                            variant="outlined"
                            inputProps={{ maxLength: 10 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="Description (Optional)"
                            value={descriptionInput}
                            onChange={(e) => setDescriptionInput(e.target.value)}
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 100 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="Logo Concept (Optional)"
                            value={logoConceptInput}
                            onChange={(e) => setLogoConceptInput(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputProps={{ maxLength: 200 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGenerate}
                            disabled={isGeneratingText || isGeneratingLogo}
                            startIcon={isGeneratingText || isGeneratingLogo ?
                                <CircularProgress size={20} color="inherit" /> :
                                <AutoAwesomeIcon />
                            }
                            sx={{
                                py: 1.5,
                                background: `linear-gradient(45deg, ${ theme.palette.primary.main }, ${ theme.palette.secondary.main })`,
                                '&:hover': {
                                    background: `linear-gradient(45deg, ${ theme.palette.primary.dark }, ${ theme.palette.secondary.dark })`,
                                },
                                '&:disabled': {
                                    background: theme.palette.action.disabledBackground,
                                }
                            }}
                        >
                            {isGeneratingText || isGeneratingLogo ? 'Generating...' : 'Generate Token Details'}
                        </Button>
                    </Stack>
                </Paper>

                {/* Error Display */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${ theme.palette.error.main }40`
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Generated Output Display Section */}
                {(finalName || finalTicker || finalDescription || generatedLogoConcept || logoUrl || isGeneratingText || isGeneratingLogo) && (
                    <Paper elevation={1} sx={{ p: 4, border: `1px solid ${ theme.palette.divider }` }}>
                        <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, mb: 3 }}>
                            Generated Token Details
                        </Typography>

                        {/* Display text generation status or results */}
                        {isGeneratingText ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                                <CircularProgress size={24} color="primary" />
                                <Typography sx={{ color: theme.palette.text.secondary }}>
                                    Generating text details...
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2} sx={{ mb: 3 }}>
                                {finalName && (
                                    <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                                        <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{finalName}</Typography>
                                    </Card>
                                )}
                                {finalTicker && (
                                    <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                                        <Typography variant="subtitle2" color="text.secondary">Ticker</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{finalTicker}</Typography>
                                    </Card>
                                )}
                                {finalDescription && (
                                    <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                                        <Typography variant="body1">{finalDescription}</Typography>
                                    </Card>
                                )}
                                {generatedLogoConcept && (
                                    <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                                        <Typography variant="subtitle2" color="text.secondary">Logo Concept</Typography>
                                        <Typography variant="body1">{generatedLogoConcept}</Typography>
                                    </Card>
                                )}
                            </Stack>
                        )}

                        {/* Logo Preview Section */}
                        {(isGeneratingLogo || logoUrl || generatedLogoConcept) && (
                            <Box>
                                <Divider sx={{ my: 3 }} />
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                        Logo Preview
                                    </Typography>
                                    {!isGeneratingLogo && generatedLogoConcept && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleGenerateLogo(generatedLogoConcept)}
                                            disabled={isGeneratingLogo}
                                            startIcon={<RefreshIcon />}
                                            sx={{
                                                borderColor: theme.palette.primary.main,
                                                color: theme.palette.primary.main,
                                                '&:hover': {
                                                    borderColor: theme.palette.primary.dark,
                                                    bgcolor: theme.palette.primary.main + '10'
                                                }
                                            }}
                                        >
                                            Regenerate
                                        </Button>
                                    )}
                                </Stack>

                                {isGeneratingLogo ? (
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            width: 200,
                                            height: 200,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            bgcolor: theme.palette.background.default
                                        }}
                                    >
                                        <Stack alignItems="center" spacing={2}>
                                            <CircularProgress size={40} color="primary" />
                                            <Typography variant="body2" color="text.secondary">
                                                Generating logo...
                                            </Typography>
                                        </Stack>
                                    </Card>
                                ) : logoUrl ? (
                                    <Card variant="outlined" sx={{ width: 200, height: 200, p: 1 }}>
                                        <Box
                                            component="img"
                                            src={`data:image/png;base64,${ logoUrl }`}
                                            alt="Generated Token Logo"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                bgcolor: theme.palette.background.paper
                                            }}
                                        />
                                    </Card>
                                ) : (
                                    generatedLogoConcept && (
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                width: 200,
                                                height: 200,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderStyle: 'dashed',
                                                bgcolor: theme.palette.background.default
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                textAlign="center"
                                                sx={{ p: 2 }}
                                            >
                                                Logo concept ready.
                                                <br />
                                                Click Regenerate to create image.
                                            </Typography>
                                        </Card>
                                    )
                                )}
                            </Box>
                        )}
                    </Paper>
                )}

                {/* Create Token Section - Shown only if logo URL is available */}
                {logoUrl && (
                    <Paper elevation={1} sx={{ p: 4, border: `1px solid ${ theme.palette.divider }` }}>
                        <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, mb: 3 }}>
                            <RocketLaunchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Create Your Token
                        </Typography>
                        <Stack spacing={3}>
                            <TextField
                                label="Amount to Buy (e.g., in BTC)"
                                value={amountInput}
                                onChange={(e) => setAmountInput(e.target.value)}
                                fullWidth
                                variant="outlined"
                                type="number"
                                inputProps={{ min: 0 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel id="wallet-select-label">Wallet / Group</InputLabel>
                                <Select
                                    labelId="wallet-select-label"
                                    label="Wallet / Group"
                                    value={selectedWalletOrGroupId}
                                    onChange={handleWalletChange}
                                    displayEmpty
                                    sx={{
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                            Select Wallet/Group
                                        </Typography>
                                    </MenuItem>

                                    {favoriteWallets.length > 0 && <ListSubheader>Favorites</ListSubheader>}
                                    {favoriteWallets.map((wallet) => (
                                        <MenuItem key={wallet._id} value={wallet._id}>
                                            {wallet.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleCreateToken}
                                disabled={isCreatingToken || !finalName || !finalTicker || !finalDescription || !logoUrl || !selectedWalletOrGroupId}
                                startIcon={isCreatingToken ?
                                    <CircularProgress size={20} color="inherit" /> :
                                    <RocketLaunchIcon />
                                }
                                sx={{
                                    py: 1.5,
                                    background: `linear-gradient(45deg, ${ theme.palette.secondary.main }, ${ theme.palette.primary.main })`,
                                    '&:hover': {
                                        background: `linear-gradient(45deg, ${ theme.palette.secondary.dark }, ${ theme.palette.primary.dark })`,
                                    },
                                    '&:disabled': {
                                        background: theme.palette.action.disabledBackground,
                                    }
                                }}
                            >
                                {isCreatingToken ? 'Creating Token...' : 'Create Token'}
                            </Button>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
}
export default CreatePage;